import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* ── Animated counter ─────────────────────────── */
function Counter({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          let start = 0;
          const step = Math.max(1, to / 60);
          const timer = setInterval(() => {
            start += step;
            if (start >= to) {
              setVal(to);
              clearInterval(timer);
            } else {
              setVal(Math.floor(start));
            }
          }, 20);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ── Particle canvas ─────────────────────────── */
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 45 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.4,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      color: Math.random() > 0.6 ? "#00f5c4" : Math.random() > 0.5 ? "#7b61ff" : "#ff6b35",
      alpha: Math.random() * 0.5 + 0.1,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.round(p.alpha * 255).toString(16).padStart(2, "0");
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", width: "100%", height: "100%" }}
    />
  );
}

/* ── Feature Card ────────────────────────────── */
function FeatureCard({ icon, title, desc, color, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="wb-feat-card"
      style={{
        background: hov ? `rgba(${color},0.08)` : "var(--surface)",
        border: `1px solid ${hov ? `rgba(${color},0.4)` : "var(--border)"}`,
        borderRadius: 12,
        padding: "28px 24px",
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        transform: hov ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hov ? `0 16px 40px rgba(0,0,0,0.5), 0 0 24px rgba(${color},0.08)` : "none",
        animation: `fadeInUp 0.6s ${delay}s ease both`,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `rgba(${color},0.15)`,
          border: `1px solid rgba(${color},0.3)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          marginBottom: 18,
          boxShadow: hov ? `0 0 16px rgba(${color},0.3)` : "none",
          transition: "all 0.3s",
        }}
      >
        {icon}
      </div>
      <h3 style={{ fontFamily: "'Orbitron', monospace", fontSize: 16, fontWeight: 700, marginBottom: 10, color: "var(--text)" }}>
        {title}
      </h3>
      <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.7, fontWeight: 500 }}>
        {desc}
      </p>
    </div>
  );
}

/* ── Leaderboard Row ──────────────────────────── */
function LeaderRow({ rank, name, city, steps, isYou, delay }) {
  const medals = ["🥇", "🥈", "🥉"];
  const golds = ["rgba(255,215,0,0.15)", "rgba(192,192,192,0.1)", "rgba(205,127,50,0.1)"];
  const isTop = rank <= 3;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: isYou ? "rgba(0,245,196,0.06)" : isTop ? golds[rank - 1] : "var(--surface)",
        border: `1px solid ${isYou ? "rgba(0,245,196,0.3)" : isTop ? "rgba(255,215,0,0.15)" : "var(--border)"}`,
        borderRadius: 10,
        padding: "14px 16px",
        animation: `slideLeft 0.5s ${delay}s ease both`,
        transition: "all 0.2s",
        width: "100%",
      }}
    >
      <div
        style={{
          width: 28,
          textAlign: "center",
          fontSize: isTop ? 16 : 13,
          fontFamily: "'Orbitron', monospace",
          fontWeight: 700,
          color: isTop ? ["#ffd700", "#c0c0c0", "#cd7f32"][rank - 1] : "var(--muted)",
          flexShrink: 0,
        }}
      >
        {isTop ? medals[rank - 1] : `#${rank}`}
      </div>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "var(--surface3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 15,
          flexShrink: 0,
        }}
      >
        {["🧑", "👩", "🧔", "👨", "🧒"][rank % 5]}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: isYou ? "var(--neon)" : "var(--text)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}{" "}
          {isYou && (
            <span
              style={{
                fontSize: 9,
                background: "var(--neon)",
                color: "var(--bg)",
                padding: "2px 5px",
                borderRadius: 4,
                fontFamily: "monospace",
                marginLeft: 4,
              }}
            >
              YOU
            </span>
          )}
        </div>
        <div style={{ color: "var(--muted)", fontSize: 11 }}>{city}</div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div
          style={{
            fontFamily: "'Orbitron', monospace",
            fontWeight: 700,
            fontSize: 14,
            color: isTop ? "#ffd700" : "var(--text)",
          }}
        >
          {steps.toLocaleString()}
        </div>
        <div style={{ color: "var(--muted)", fontSize: 10 }}>steps</div>
      </div>
    </div>
  );
}

/* ── Step Tracker Mock ───────────────────────── */
function StepTracker() {
  const [steps, setSteps] = useState(6420);
  const goal = 10000;
  const pct = Math.min((steps / goal) * 100, 100);
  useEffect(() => {
    const t = setInterval(() => setSteps((s) => (s < 10000 ? s + Math.floor(Math.random() * 8 + 1) : s)), 1200);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: 24,
        animation: "scaleIn 0.6s ease both",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: "var(--muted)",
              letterSpacing: 2,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Live Steps
          </div>
          <div
            style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: "clamp(26px, 5vw, 34px)",
              fontWeight: 900,
              color: "var(--neon)",
              textShadow: "0 0 20px rgba(0,245,196,0.4)",
            }}
          >
            {steps.toLocaleString()}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Goal</div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
            10,000
          </div>
        </div>
      </div>
      <div style={{ height: 8, background: "var(--surface3)", borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "linear-gradient(90deg, var(--neon), #00d4a8)",
            borderRadius: 4,
            transition: "width 0.8s ease",
            boxShadow: "0 0 10px rgba(0,245,196,0.5)",
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <span style={{ color: "var(--neon)", fontWeight: 700 }}>{pct.toFixed(1)}% Complete</span>
        <span style={{ color: "var(--muted)" }}>{(goal - steps).toLocaleString()} to go</span>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 18 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center" }}>
            <div
              style={{
                height: 36,
                background: i < 5 ? "rgba(0,245,196,0.15)" : "var(--surface3)",
                borderRadius: 4,
                marginBottom: 4,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: `${[80, 60, 100, 45, 65, 0, 0][i]}%`,
                  background: i < 5 ? "var(--neon)" : "var(--surface3)",
                  borderRadius: "4px 4px 0 0",
                  opacity: i < 5 ? 0.7 : 0.3,
                }}
              />
            </div>
            <span style={{ fontSize: 10, color: i < 5 ? "var(--neon)" : "var(--muted)" }}>{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Battle Card Mock ────────────────────────── */
function BattleCard({ title, challenger, opponent, cSteps, oSteps, status, delay }) {
  const total = cSteps + oSteps;
  const cPct = (cSteps / total) * 100;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid rgba(255,107,53,0.15)",
        borderRadius: 12,
        padding: 20,
        animation: `fadeInUp 0.5s ${delay}s ease both`,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: "var(--neon2)",
            letterSpacing: 2,
            textTransform: "uppercase",
          }}
        >
          ⚔️ Battle
        </span>
        <span
          style={{
            fontSize: 10,
            background: status === "LIVE" ? "rgba(255,69,103,0.15)" : "rgba(0,245,196,0.1)",
            color: status === "LIVE" ? "var(--danger)" : "var(--neon)",
            padding: "3px 8px",
            borderRadius: 100,
            border: `1px solid ${status === "LIVE" ? "rgba(255,69,103,0.3)" : "rgba(0,245,196,0.2)"}`,
            fontFamily: "monospace",
            fontWeight: 700,
          }}
        >
          {status === "LIVE" ? "🔴 LIVE" : "✅ DONE"}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 14 }}>{title}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: "var(--neon)", fontWeight: 700 }}>{challenger}</span>
        <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "monospace" }}>VS</span>
        <span style={{ fontSize: 13, color: "var(--neon2)", fontWeight: 700 }}>{opponent}</span>
      </div>
      <div style={{ height: 6, background: "var(--surface3)", borderRadius: 3, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${cPct}%`,
            background: "linear-gradient(90deg,var(--neon),var(--neon2))",
            borderRadius: 3,
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 8,
          fontSize: 12,
          fontFamily: "'Orbitron', monospace",
        }}
      >
        <span style={{ color: "var(--neon)" }}>{cSteps.toLocaleString()}</span>
        <span style={{ color: "var(--neon2)" }}>{oSteps.toLocaleString()}</span>
      </div>
    </div>
  );
}

/* ── MAIN HOME ────────────────────────────────── */
export default function Home() {
  const [activeTab, setActiveTab] = useState("steps");

  const features = [
    { icon: "👟", title: "Live Step Tracking", desc: "GPS-powered real-time step and distance tracking with streak bonuses and daily goals.", color: "0,245,196", delay: 0.1 },
    { icon: "⚔️", title: "Territory Battles", desc: "Claim paths by walking them more than anyone else. Defend your turf or conquer new zones.", color: "255,107,53", delay: 0.15 },
    { icon: "🏆", title: "City Leaderboard", desc: "Compete across distance, territory count, and streak. Rise to the top of your city's ranks.", color: "123,97,255", delay: 0.2 },
    { icon: "🎖️", title: "Rewards & Coins", desc: "Earn coins for every milestone. Unlock badges, claim rewards, and cash in your victories.", color: "255,215,0", delay: 0.25 },
    { icon: "🗺️", title: "Live Map Territories", desc: "Watch your territories glow on the live city map. See enemies approaching in real-time.", color: "0,245,196", delay: 0.3 },
    { icon: "👥", title: "Social Challenges", desc: "Follow rivals, challenge friends, and build your squad. Walking is better with competition.", color: "255,107,53", delay: 0.35 },
  ];

  const leaderboard = [
    { rank: 1, name: "ArjunWalker", city: "Delhi", steps: 84321 },
    { rank: 2, name: "PriyaRuns", city: "Mumbai", steps: 79104 },
    { rank: 3, name: "ViratSteps", city: "Bangalore", steps: 72890 },
    { rank: 4, name: "You", city: "Your City", steps: 68450, isYou: true },
    { rank: 5, name: "NehaWalks", city: "Chennai", steps: 61200 },
  ];

  const battles = [
    { title: "Connaught Place Takeover", challenger: "You", opponent: "SunilK", cSteps: 8420, oSteps: 6100, status: "LIVE", delay: 0 },
    { title: "South Delhi Route", challenger: "RohitM", opponent: "AmitP", cSteps: 12400, oSteps: 9800, status: "DONE", delay: 0.1 },
    { title: "MG Road Territory", challenger: "You", opponent: "AnanyaS", cSteps: 5200, oSteps: 7100, status: "LIVE", delay: 0.2 },
  ];

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", position: "relative", width: "100%", overflowX: "hidden" }}>
      <Particles />

      {/* ── Background Grid ── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(0,245,196,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,196,0.025) 1px,transparent 1px)",
          backgroundSize: "60px 60px",
          animation: "gridMove 25s linear infinite",
        }}
      />

      <style>{`
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .wb-ticker-track {
          display: flex;
          gap: 48px;
          width: max-content;
          animation: ticker 30s linear infinite;
          will-change: transform;
        }

        .wb-ticker-track:hover {
          animation-play-state: paused;
        }

        .wb-hero-section {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 48px 80px;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        .wb-hero-cols {
          display: flex;
          align-items: center;
          gap: 64px;
          max-width: 1200px;
          width: 100%;
          margin: 0 auto;
        }

        .wb-hero-left {
          flex: 1.1;
        }

        .wb-hero-right {
          flex: 0.9;
          display: flex;
          justify-content: center;
          position: relative;
        }

        .wb-hero-btns {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
        }

        .wb-hero-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: var(--neon);
          color: var(--bg);
          padding: 15px 30px;
          border-radius: 6px;
          font-family: 'Orbitron', monospace;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 2px;
          text-decoration: none;
          transition: all 0.3s;
          text-transform: uppercase;
          box-shadow: 0 0 20px rgba(0, 245, 196, 0.3);
        }

        .wb-hero-btn-primary:hover {
          box-shadow: 0 0 35px rgba(0, 245, 196, 0.6);
          transform: translateY(-2px);
        }

        .wb-hero-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.03);
          color: var(--text);
          padding: 15px 30px;
          border-radius: 6px;
          font-family: 'Orbitron', monospace;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          text-decoration: none;
          transition: all 0.3s;
          text-transform: uppercase;
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        .wb-hero-btn-secondary:hover {
          border-color: var(--neon3);
          color: var(--neon3);
          background: rgba(123, 97, 255, 0.08);
        }

        .wb-section {
          padding: 80px 48px;
          position: relative;
          z-index: 1;
        }

        .wb-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-how-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          position: relative;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-how-line {
          position: absolute;
          top: 36px;
          left: 18%;
          right: 18%;
          height: 1px;
          background: linear-gradient(90deg, var(--neon), var(--neon3), var(--neon2));
          opacity: 0.35;
        }

        .wb-rewards-cols {
          display: flex;
          gap: 56px;
          align-items: center;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-rewards-left, .wb-rewards-right {
          flex: 1;
          width: 100%;
        }

        .wb-testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .wb-cta-box {
          max-width: 960px;
          margin: 0 auto;
          background: linear-gradient(135deg, rgba(0, 245, 196, 0.08), rgba(123, 97, 255, 0.08));
          border: 1px solid rgba(0, 245, 196, 0.2);
          border-radius: 20px;
          padding: 60px 48px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .wb-tab-btn {
          border: none;
          cursor: pointer;
          transition: all 0.25s;
          font-family: 'Orbitron', monospace;
          font-size: 11px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          font-weight: 700;
          border-radius: 6px;
          padding: 10px 22px;
        }
        .wb-tab-btn.active {
          background: var(--neon);
          color: var(--bg);
          box-shadow: 0 0 16px rgba(0, 245, 196, 0.4);
        }
        .wb-tab-btn:not(.active) {
          background: var(--surface2);
          color: var(--muted);
        }
        .wb-tab-btn:not(.active):hover {
          color: var(--neon);
          background: var(--surface3);
        }

        /* ── TABLET BREAKPOINT (769px - 1024px) ── */
        @media (max-width: 1024px) {
          .wb-hero-section {
            padding: 110px 32px 64px;
          }
          .wb-hero-cols {
            gap: 40px;
          }
          .wb-section {
            padding: 64px 32px;
          }
          .wb-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }
          .wb-features-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }
          .wb-testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .wb-rewards-cols {
            flex-direction: column;
            gap: 40px;
          }
        }

        /* ── MOBILE / SMALL TABLET BREAKPOINT (< 860px) ── */
        @media (max-width: 860px) {
          .wb-hero-cols {
            flex-direction: column;
            text-align: center;
            gap: 48px;
          }
          .wb-hero-left {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .wb-hero-btns {
            justify-content: center;
          }
          .wb-trust-badges {
            justify-content: center !important;
          }
          .wb-how-grid {
            grid-template-columns: 1fr;
            gap: 36px;
          }
          .wb-how-line {
            display: none;
          }
          .wb-how-step-card {
            padding: 0 16px !important;
          }
          .wb-testimonials-grid {
            grid-template-columns: 1fr;
          }
        }

        /* ── MOBILE BREAKPOINT (< 640px) ── */
        @media (max-width: 640px) {
          .wb-hero-section {
            padding: 90px 20px 48px;
          }
          .wb-section {
            padding: 52px 20px;
          }
          .wb-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .wb-features-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .wb-cta-box {
            padding: 40px 20px;
          }
          .wb-hero-btns {
            flex-direction: column;
            width: 100%;
          }
          .wb-hero-btn-primary, .wb-hero-btn-secondary {
            width: 100%;
          }
          .wb-floating-badge-1 {
            right: 0 !important;
            top: 10px !important;
            transform: scale(0.9);
          }
          .wb-floating-badge-2 {
            left: 0 !important;
            bottom: 40px !important;
            transform: scale(0.9);
          }
          .wb-hero-rings {
            display: none;
          }
        }

        /* ── EXTRA SMALL MOBILE (< 400px) ── */
        @media (max-width: 400px) {
          .wb-stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* ════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════ */}
      <section className="wb-hero-section">
        {/* Glow blob */}
        <div
          style={{
            position: "absolute",
            top: "40%",
            left: "50%",
            width: "min(600px, 90vw)",
            height: "min(600px, 90vw)",
            background: "radial-gradient(circle, rgba(0,245,196,0.07) 0%, transparent 70%)",
            transform: "translate(-50%,-50%)",
            pointerEvents: "none",
            animation: "glow 5s ease-in-out infinite",
          }}
        />

        <div className="wb-hero-cols">
          {/* Left Column */}
          <div className="wb-hero-left">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(0,245,196,0.07)",
                border: "1px solid rgba(0,245,196,0.2)",
                padding: "6px 16px",
                borderRadius: 100,
                marginBottom: 24,
                animation: "fadeInDown 0.7s ease both",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "var(--neon)",
                  borderRadius: "50%",
                  animation: "blink 1.2s infinite",
                  boxShadow: "0 0 8px var(--neon)",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--neon)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Now Live • 50,000+ Warriors
              </span>
            </div>

            <h1
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(34px, 6.5vw, 76px)",
                fontWeight: 900,
                lineHeight: 1.05,
                marginBottom: 20,
                animation: "fadeInUp 0.8s 0.1s ease both",
              }}
            >
              <span style={{ display: "block", color: "var(--text)" }}>Compete.</span>
              <span style={{ display: "block", color: "var(--text)" }}>Walk.</span>
              <span
                style={{
                  display: "block",
                  background: "linear-gradient(90deg,var(--neon),var(--neon3),var(--neon2))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  backgroundSize: "200%",
                  animation: "gradShift 3s linear infinite",
                }}
              >
                Rule.
              </span>
            </h1>

            <p
              style={{
                fontSize: "clamp(15px, 2vw, 18px)",
                color: "var(--muted)",
                lineHeight: 1.75,
                maxWidth: 480,
                marginBottom: 36,
                fontWeight: 500,
                animation: "fadeInUp 0.8s 0.25s ease both",
              }}
            >
              Turn every step into territory. Claim city paths, crush rivals in real-time battles, and dominate your city's leaderboard.
            </p>

            <div className="wb-hero-btns" style={{ animation: "fadeInUp 0.8s 0.4s ease both" }}>
              <a href="#features" className="wb-hero-btn-primary">
                ▶ Start Walking
              </a>
              <a href="#how" className="wb-hero-btn-secondary">
                Watch Demo
              </a>
            </div>

            {/* Trust badges */}
            <div
              className="wb-trust-badges"
              style={{
                display: "flex",
                gap: 20,
                marginTop: 40,
                flexWrap: "wrap",
                animation: "fadeInUp 0.8s 0.55s ease both",
              }}
            >
              {[
                ["⭐", "4.9 Rating", "App Store"],
                ["👟", "50K+", "Active Warriors"],
                ["🏙️", "200+", "Cities"],
              ].map(([ic, val, lbl]) => (
                <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 22 }}>{ic}</div>
                  <div>
                    <div style={{ fontFamily: "'Orbitron', monospace", fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                      {val}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{lbl}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column — Phone Mockup */}
          <div className="wb-hero-right" style={{ animation: "scaleIn 0.8s 0.3s ease both" }}>
            <div style={{ position: "relative", maxWidth: "100%" }}>
              {/* Outer decorative rings (Hidden on small mobile) */}
              <div className="wb-hero-rings">
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 340,
                    height: 340,
                    border: "1px solid rgba(0,245,196,0.12)",
                    borderRadius: "50%",
                    transform: "translate(-50%,-50%)",
                    animation: "rotateRing 20s linear infinite",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 420,
                    height: 420,
                    border: "1px dashed rgba(123,97,255,0.08)",
                    borderRadius: "50%",
                    transform: "translate(-50%,-50%)",
                    animation: "rotateRing 30s linear infinite reverse",
                  }}
                />
              </div>

              {/* Phone Frame */}
              <div
                style={{
                  width: 250,
                  maxWidth: "85vw",
                  height: 500,
                  background: "var(--surface)",
                  border: "2px solid rgba(0,245,196,0.25)",
                  borderRadius: 36,
                  padding: "16px 14px",
                  boxShadow: "0 0 50px rgba(0,245,196,0.12), 0 30px 60px rgba(0,0,0,0.6)",
                  position: "relative",
                  overflow: "hidden",
                  margin: "0 auto",
                }}
              >
                {/* Notch */}
                <div
                  style={{
                    width: 70,
                    height: 18,
                    background: "var(--bg)",
                    borderRadius: 10,
                    margin: "0 auto 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ width: 32, height: 3, background: "var(--surface3)", borderRadius: 2 }} />
                </div>

                {/* App screen content */}
                <div
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    fontSize: 9,
                    color: "var(--muted)",
                    letterSpacing: 1,
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  WalkBattle
                </div>

                <div
                  style={{
                    background: "linear-gradient(135deg,rgba(0,245,196,0.1),rgba(123,97,255,0.1))",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 8, color: "var(--muted)", marginBottom: 2, fontFamily: "monospace" }}>
                    TODAY'S STEPS
                  </div>
                  <div
                    style={{
                      fontFamily: "'Orbitron', monospace",
                      fontSize: 24,
                      fontWeight: 900,
                      color: "var(--neon)",
                      textShadow: "0 0 12px rgba(0,245,196,0.5)",
                    }}
                  >
                    8,420
                  </div>
                  <div style={{ height: 4, background: "var(--surface3)", borderRadius: 2, marginTop: 6 }}>
                    <div
                      style={{
                        height: "100%",
                        width: "84%",
                        background: "var(--neon)",
                        borderRadius: 2,
                        boxShadow: "0 0 6px rgba(0,245,196,0.5)",
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    fontSize: 8,
                    color: "var(--muted)",
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Active Battles
                </div>
                {["Connaught Pl.", "MG Road"].map((b, i) => (
                  <div
                    key={b}
                    style={{
                      background: "var(--surface2)",
                      borderRadius: 8,
                      padding: "6px 8px",
                      marginBottom: 5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 10, color: "var(--text)", fontWeight: 600 }}>{b}</span>
                    <span
                      style={{
                        fontSize: 8,
                        color: i === 0 ? "var(--neon)" : "var(--neon2)",
                        fontFamily: "monospace",
                        background: `rgba(${i === 0 ? "0,245,196" : "255,107,53"},0.1)`,
                        padding: "2px 5px",
                        borderRadius: 4,
                      }}
                    >
                      {i === 0 ? "WINNING" : "CLOSE"}
                    </span>
                  </div>
                ))}

                <div
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    fontSize: 8,
                    color: "var(--muted)",
                    marginBottom: 6,
                    marginTop: 8,
                    textTransform: "uppercase",
                  }}
                >
                  Leaderboard
                </div>
                {["#1 ArjunW · 84k", "#2 PriyaR · 79k", "#4 You · 68k"].map((r, i) => (
                  <div key={r} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 11 }}>{["🥇", "🥈", "👤"][i]}</span>
                    <span
                      style={{
                        fontSize: 9,
                        color: i === 2 ? "var(--neon)" : "var(--muted)",
                        fontFamily: "monospace",
                      }}
                    >
                      {r}
                    </span>
                  </div>
                ))}

                {/* Bottom nav */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 16,
                    left: 14,
                    right: 14,
                    display: "flex",
                    justifyContent: "space-around",
                  }}
                >
                  {["🏠", "⚔️", "🗺️", "🏆", "👤"].map((ic, i) => (
                    <div key={i} style={{ fontSize: i === 0 ? 18 : 14, opacity: i === 0 ? 1 : 0.4 }}>
                      {ic}
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badges */}
              <div
                className="wb-floating-badge-1"
                style={{
                  position: "absolute",
                  top: 20,
                  right: -45,
                  background: "var(--surface)",
                  border: "1px solid rgba(0,245,196,0.3)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  whiteSpace: "nowrap",
                  animation: "float 3s ease-in-out infinite",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                }}
              >
                <div style={{ fontSize: 10, color: "var(--neon)", fontFamily: "monospace", fontWeight: 700 }}>
                  +500 COINS
                </div>
                <div style={{ fontSize: 9, color: "var(--muted)" }}>Territory Claimed!</div>
              </div>
              <div
                className="wb-floating-badge-2"
                style={{
                  position: "absolute",
                  bottom: 60,
                  left: -50,
                  background: "var(--surface)",
                  border: "1px solid rgba(255,107,53,0.35)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  whiteSpace: "nowrap",
                  animation: "float 4s 1s ease-in-out infinite",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                }}
              >
                <div style={{ fontSize: 10, color: "var(--neon2)", fontFamily: "monospace", fontWeight: 700 }}>
                  ⚔️ BATTLE START
                </div>
                <div style={{ fontSize: 9, color: "var(--muted)" }}>Challenge received!</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Scrolling Ticker ── */}
      <div
        style={{
          overflow: "hidden",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
          background: "rgba(0,245,196,0.03)",
          padding: "14px 0",
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        <div className="wb-ticker-track">
          {[...Array(4)].flatMap((_, ri) =>
            [
              "⚔️ Battle Started · Delhi",
              "🏆 New #1 · Mumbai",
              "👟 50K Steps · Bangalore",
              "🗺️ Territory Claimed · Pune",
              "🔥 10-Day Streak · Hyderabad",
              "🎖️ Badge Earned · Chennai",
              "⚔️ Battle Won · Noida",
              "🏙️ 200 Cities · Live Now",
            ].map((t, i) => (
              <span
                key={`${ri}-${i}`}
                style={{
                  color: "var(--muted)",
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {t}
              </span>
            ))
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          STATS ROW
      ════════════════════════════════════════════════ */}
      <section className="wb-section">
        <div className="wb-stats-grid">
          {[
            { val: 50000, suffix: "+", label: "Active Warriors", color: "var(--neon)", icon: "👟" },
            { val: 200, suffix: "+", label: "Cities Covered", color: "var(--neon3)", icon: "🏙️" },
            { val: 12000, suffix: "+", label: "Battles Fought", color: "var(--neon2)", icon: "⚔️" },
            { val: 500, suffix: "M+", label: "Steps Tracked", color: "var(--gold)", icon: "📊" },
          ].map(({ val, suffix, label, color, icon }) => (
            <div
              key={label}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "24px 18px",
                textAlign: "center",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(0,245,196,0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
            >
              <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
              <div
                style={{
                  fontFamily: "'Orbitron', monospace",
                  fontSize: "clamp(24px, 4vw, 32px)",
                  fontWeight: 900,
                  color,
                  marginBottom: 4,
                }}
              >
                <Counter to={val} suffix={suffix} />
              </div>
              <div style={{ color: "var(--muted)", fontSize: 13, fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FEATURES
      ════════════════════════════════════════════════ */}
      <section id="features" className="wb-section">
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 24, height: 1, background: "var(--neon)", display: "inline-block" }} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--neon)",
                  letterSpacing: 3,
                  textTransform: "uppercase",
                }}
              >
                Features
              </span>
              <span style={{ width: 24, height: 1, background: "var(--neon)", display: "inline-block" }} />
            </div>
            <h2
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(26px, 4.5vw, 46px)",
                fontWeight: 900,
                marginBottom: 14,
                color: "var(--text)",
              }}
            >
              Built for{" "}
              <span
                style={{
                  background: "linear-gradient(90deg,var(--neon),var(--neon3))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Warriors
              </span>
            </h2>
            <p style={{ color: "var(--muted)", fontSize: "clamp(14px, 2vw, 16px)", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
              Every feature designed to push you harder, compete fiercer, and walk further than ever before.
            </p>
          </div>
          <div className="wb-features-grid">
            {features.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          INTERACTIVE DEMO TABS
      ════════════════════════════════════════════════ */}
      <section
        id="how"
        className="wb-section"
        style={{ background: "linear-gradient(180deg,transparent,rgba(14,21,32,0.8),transparent)" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 24, height: 1, background: "var(--neon2)", display: "inline-block" }} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--neon2)",
                  letterSpacing: 3,
                  textTransform: "uppercase",
                }}
              >
                Live Demo
              </span>
              <span style={{ width: 24, height: 1, background: "var(--neon2)", display: "inline-block" }} />
            </div>
            <h2
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(26px, 4.5vw, 46px)",
                fontWeight: 900,
                color: "var(--text)",
                marginBottom: 14,
              }}
            >
              See It in <span style={{ color: "var(--neon2)" }}>Action</span>
            </h2>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 32, flexWrap: "wrap" }}>
            {[
              ["steps", "👟 Steps"],
              ["battles", "⚔️ Battles"],
              ["leaderboard", "🏆 Leaderboard"],
            ].map(([id, label]) => (
              <button
                key={id}
                className={`wb-tab-btn ${activeTab === id ? "active" : ""}`}
                onClick={() => setActiveTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {activeTab === "steps" && (
            <div style={{ maxWidth: 480, margin: "0 auto", width: "100%", animation: "scaleIn 0.4s ease both" }}>
              <StepTracker />
            </div>
          )}

          {activeTab === "battles" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                gap: 16,
                maxWidth: 1000,
                margin: "0 auto",
                width: "100%",
              }}
            >
              {battles.map((b) => (
                <BattleCard key={b.title} {...b} />
              ))}
            </div>
          )}

          {activeTab === "leaderboard" && (
            <div
              style={{
                maxWidth: 560,
                margin: "0 auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {leaderboard.map((r, i) => (
                <LeaderRow key={r.rank} {...r} delay={i * 0.06} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          HOW IT WORKS
      ════════════════════════════════════════════════ */}
      <section className="wb-section">
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 24, height: 1, background: "var(--neon3)", display: "inline-block" }} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--neon3)",
                  letterSpacing: 3,
                  textTransform: "uppercase",
                }}
              >
                How It Works
              </span>
              <span style={{ width: 24, height: 1, background: "var(--neon3)", display: "inline-block" }} />
            </div>
            <h2
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(26px, 4.5vw, 46px)",
                fontWeight: 900,
                color: "var(--text)",
              }}
            >
              3 Steps to <span style={{ color: "var(--neon3)" }}>Domination</span>
            </h2>
          </div>

          <div className="wb-how-grid">
            <div className="wb-how-line" />
            {[
              {
                step: "01",
                icon: "📲",
                title: "Download & Join",
                desc: "Create your warrior profile. Pick your city. Set your daily step goal and enter the arena.",
                color: "var(--neon)",
              },
              {
                step: "02",
                icon: "🚶",
                title: "Walk & Capture",
                desc: "Every path you walk gets claimed. Walk it 5 days in a row to fully own the territory.",
                color: "var(--neon3)",
              },
              {
                step: "03",
                icon: "🏆",
                title: "Battle & Win",
                desc: "Challenge rivals, defend your turf, climb the city leaderboard. Earn coins and badges.",
                color: "var(--neon2)",
              },
            ].map(({ step, icon, title, desc, color }, i) => (
              <div
                key={step}
                className="wb-how-step-card"
                style={{
                  padding: "0 24px",
                  textAlign: "center",
                  animation: `fadeInUp 0.6s ${i * 0.15}s ease both`,
                }}
              >
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    background: "var(--surface)",
                    border: `2px solid ${color}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 26,
                    margin: "0 auto 20px",
                    boxShadow: `0 0 20px rgba(0,0,0,0.3), 0 0 24px rgba(0,245,196,0.15)`,
                    position: "relative",
                  }}
                >
                  {icon}
                  <div
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 22,
                      height: 22,
                      background: color,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "'Orbitron', monospace",
                      fontSize: 9,
                      fontWeight: 900,
                      color: "var(--bg)",
                    }}
                  >
                    {step}
                  </div>
                </div>
                <h3
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "var(--text)",
                    marginBottom: 10,
                  }}
                >
                  {title}
                </h3>
                <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          STREAK / GAMIFICATION SECTION
      ════════════════════════════════════════════════ */}
      <section
        className="wb-section"
        style={{ background: "linear-gradient(180deg,transparent,rgba(14,21,32,0.6),transparent)" }}
      >
        <div className="wb-rewards-cols">
          <div className="wb-rewards-left">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 24, height: 1, background: "var(--gold)", display: "inline-block" }} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--gold)",
                  letterSpacing: 3,
                  textTransform: "uppercase",
                }}
              >
                Rewards
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(26px, 4vw, 44px)",
                fontWeight: 900,
                color: "var(--text)",
                marginBottom: 18,
                lineHeight: 1.1,
              }}
            >
              Every Step
              <br />
              <span style={{ color: "var(--gold)" }}>Earns Gold</span>
            </h2>
            <p style={{ color: "var(--muted)", fontSize: "clamp(14px, 2vw, 16px)", lineHeight: 1.75, marginBottom: 32, maxWidth: 440 }}>
              Streaks multiply your coins. Claim badges for milestones. Spend your earned rewards on real prizes. Walking has never been this rewarding.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { icon: "🔥", label: "7-Day Streak", val: "+2x Coins", color: "var(--neon2)" },
                { icon: "🗺️", label: "Territory Owned", val: "+100 Coins/day", color: "var(--neon)" },
                { icon: "⚔️", label: "Battle Victory", val: "+500 Coins", color: "var(--neon3)" },
              ].map(({ icon, label, val, color }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: "12px 18px",
                  }}
                >
                  <span style={{ fontSize: 20 }}>{icon}</span>
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{label}</span>
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 12, fontWeight: 700, color }}>
                    {val}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Streak visual */}
          <div className="wb-rewards-right">
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "28px 24px" }}>
              <div
                style={{
                  fontFamily: "'Orbitron', monospace",
                  fontSize: 11,
                  color: "var(--muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                Weekly Streak
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center" }}>
                    <div
                      style={{
                        width: "100%",
                        paddingTop: "100%",
                        borderRadius: "50%",
                        position: "relative",
                        background: i < 5 ? "linear-gradient(135deg,var(--neon),var(--neon3))" : "var(--surface3)",
                        border: i < 5 ? "none" : "1px solid var(--border)",
                        marginBottom: 6,
                        boxShadow: i < 5 ? "0 0 10px rgba(0,245,196,0.35)" : "none",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: i < 5 ? 13 : 11,
                          color: i < 5 ? "var(--bg)" : "var(--muted)",
                          fontWeight: 700,
                        }}
                      >
                        {i < 5 ? "✓" : d}
                      </span>
                    </div>
                    <span style={{ fontSize: 10, color: i < 5 ? "var(--neon)" : "var(--muted)" }}>{d}</span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  background: "linear-gradient(135deg,rgba(255,215,0,0.08),rgba(255,107,53,0.08))",
                  border: "1px solid rgba(255,215,0,0.15)",
                  borderRadius: 10,
                  padding: 16,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 6 }}>🔥</div>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 28, fontWeight: 900, color: "var(--gold)" }}>
                  5
                </div>
                <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>Day Streak · 2x Coins Active</div>
              </div>

              {/* Badge row */}
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                {["🥇 First Win", "🗺️ Explorer", "💯 100K Club"].map((b) => (
                  <div
                    key={b}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      background: "var(--surface2)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "8px 4px",
                      fontSize: 10,
                      color: "var(--muted)",
                    }}
                  >
                    {b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          TESTIMONIALS
      ════════════════════════════════════════════════ */}
      <section className="wb-section">
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 24, height: 1, background: "var(--neon)", display: "inline-block" }} />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--neon)",
                  letterSpacing: 3,
                  textTransform: "uppercase",
                }}
              >
                Warriors Speak
              </span>
              <span style={{ width: 24, height: 1, background: "var(--neon)", display: "inline-block" }} />
            </div>
            <h2
              style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "clamp(26px, 4vw, 44px)",
                fontWeight: 900,
                color: "var(--text)",
              }}
            >
              What People Say
            </h2>
          </div>
          <div className="wb-testimonials-grid">
            {[
              { name: "Arjun S.", city: "Delhi", quote: "I walk 12km a day now just to defend my territories. WalkBattle literally changed my life.", stars: 5, avatar: "👨" },
              { name: "Priya M.", city: "Mumbai", quote: "The battle system is insane. I challenged my office rival and now we both walk 10k steps daily!", stars: 5, avatar: "👩" },
              { name: "Rahul K.", city: "Bangalore", quote: "I lost 8kg in 3 months just by competing on leaderboard. Best fitness app I've ever used.", stars: 5, avatar: "🧔" },
            ].map(({ name, city, quote, stars, avatar }, i) => (
              <div
                key={name}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  padding: 24,
                  animation: `fadeInUp 0.6s ${i * 0.1}s ease both`,
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.borderColor = "rgba(0,245,196,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "var(--surface3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                    }}
                  >
                    {avatar}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{city}</div>
                  </div>
                  <div style={{ marginLeft: "auto", color: "var(--gold)", fontSize: 12 }}>{"★".repeat(stars)}</div>
                </div>
                <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.7, fontStyle: "italic" }}>
                  "{quote}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CTA BANNER
      ════════════════════════════════════════════════ */}
      <section className="wb-section">
        <div className="wb-cta-box">
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 350,
              height: 350,
              background: "radial-gradient(circle,rgba(0,245,196,0.06),transparent 70%)",
              transform: "translate(-50%,-50%)",
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: "var(--neon)",
              letterSpacing: 3,
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            [ READY TO DOMINATE? ]
          </div>
          <h2
            style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: "clamp(26px, 4.5vw, 48px)",
              fontWeight: 900,
              color: "var(--text)",
              marginBottom: 16,
              lineHeight: 1.15,
            }}
          >
            Own Your Path.
            <br />
            <span
              style={{
                background: "linear-gradient(90deg,var(--neon),var(--neon3))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Rule Your City.
            </span>
          </h2>
          <p style={{ color: "var(--muted)", fontSize: "clamp(14px, 2vw, 16px)", marginBottom: 36, lineHeight: 1.7, maxWidth: 540, margin: "0 auto 36px" }}>
            Join 50,000+ warriors already walking their city into submission. Download free. No subscriptions. Pure competition.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link
              to="/contact"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                background: "var(--neon)",
                color: "var(--bg)",
                padding: "16px 36px",
                borderRadius: 6,
                fontFamily: "'Orbitron', monospace",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 2,
                textDecoration: "none",
                transition: "all 0.3s",
                textTransform: "uppercase",
                boxShadow: "0 0 25px rgba(0,245,196,0.35)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 0 45px rgba(0,245,196,0.6)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 0 25px rgba(0,245,196,0.35)";
                e.currentTarget.style.transform = "none";
              }}
            >
              📲 Download Now — Free
            </Link>
            <Link
              to="/features"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                background: "transparent",
                color: "var(--text)",
                padding: "16px 36px",
                borderRadius: 6,
                fontFamily: "'Orbitron', monospace",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2,
                textDecoration: "none",
                transition: "all 0.3s",
                textTransform: "uppercase",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--neon3)";
                e.currentTarget.style.color = "var(--neon3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                e.currentTarget.style.color = "var(--text)";
              }}
            >
              Explore Features →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
